import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  HostListener,
} from '@angular/core';

@Component({
    selector: 'multi-select-input',
    templateUrl: './multi-select-input.component.html',
    styleUrls: ['./multi-select-input.component.scss'],
    standalone: false
})
export class MultiSelectInputComponent<T> implements AfterViewInit {
  @Input() label = '';
  @Input() floatingLabel = false;
  @Input() options: T[] = [];
  @Input() values: T[] = [];
  @Input() placeholder = 'All';
  @Input() ariaLabel = 'Multi select options';
  @Input() displayFn: (value: T) => string = (value) => String(value); // Default display function that converts the value to a string
  @Input() compareWith: (a: T, b: T) => boolean = (a, b) => a === b; // Default comparison function that checks for strict equality
  @Input() multiSelectionSummarySuffix = '';
  @Input() summaryFn?: (values: T[], displayFn: (value: T) => string) => string; // Optional custom summary function for generating the summary text when multiple options are selected
  @Input() noOptionsMessage: string = "No options available";
  
  @Output() valuesChange = new EventEmitter<T[]>();

  @ViewChild('detailsElementRef', { static: true })
  detailsElementRef!: ElementRef<HTMLDetailsElement>;

  /** Determines whether dropdown opens upward or downward */
  dropdownOpenDirection: 'up' | 'down' = 'down';

  /** Must match max-h-48 (12rem ≈ 192px) */
  private readonly dropdownMaxHeightPx = 192;

  get hasValue(): boolean {
    return this.values.length > 0;
  }

  /** Summary to display in the input field when one or more options are selected.
   * If `summaryFn` is provided, it will be used to generate the summary.
   * Otherwise, the summary will be generated based on the number of selected options and the `multiSelectionSummarySuffix`.
   */
  get selectedSummary(): string {
    if (!this.hasValue) {
      return this.placeholder;
    }

    if (this.summaryFn) {
      return this.summaryFn(this.values, this.displayFn);
    }

    if (this.values.length === 1) {
      return this.displayFn(this.values[0]);
    }

    if (this.multiSelectionSummarySuffix.trim().length > 0) {
      return `${this.values.length} ${this.multiSelectionSummarySuffix}`;
    }

    return this.values.map((value) => this.displayFn(value)).join(', ');
  }

  ngAfterViewInit(): void {
    this.detailsElementRef.nativeElement.addEventListener('toggle', () => {
      if (this.detailsElementRef.nativeElement.open) {
        this.updateDropdownOpenDirection();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement | null;
    if (!targetElement) {
      return;
    }

    const detailsElement = this.detailsElementRef.nativeElement;
    if (detailsElement.open && !detailsElement.contains(targetElement)) {
      this.closeDropdown();
    }
  }

  isSelected(option: T): boolean {
    return this.values.some((selectedValue) =>
      this.compareWith(selectedValue, option),
    );
  }

  toggleOption(option: T, checked: boolean): void {
    const nextValues = checked
      ? [...this.values, option]
      : this.values.filter(
          (selectedValue) => !this.compareWith(selectedValue, option),
        );

    this.valuesChange.emit(nextValues);
  }

  /** Handles click event on the summary element to toggle the dropdown open/close state. */
  onSummaryClick(event: MouseEvent): void {
    event.preventDefault();

    const detailsElement = this.detailsElementRef.nativeElement;

    if (detailsElement.open) {
      // CLOSE on second click
      detailsElement.removeAttribute('open');
      return;
    }

    // OPEN (no flicker)
    this.updateDropdownOpenDirection();
    detailsElement.open = true;
  }

  closeDropdown(): void {
    this.detailsElementRef.nativeElement.removeAttribute('open');
  }

  /**
   * Determines whether the dropdown should open upwards or downwards based on the available space in the viewport.
   */
  private updateDropdownOpenDirection(): void {
    const detailsBoundingRect =
      this.detailsElementRef.nativeElement.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const availableSpaceBelow = viewportHeight - detailsBoundingRect.bottom;
    const availableSpaceAbove = detailsBoundingRect.top;

    this.dropdownOpenDirection =
      availableSpaceBelow < this.dropdownMaxHeightPx &&
      availableSpaceAbove > availableSpaceBelow
        ? 'up'
        : 'down';
  }
}
